import { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	useTransactionsQuery,
	useCategoriesQuery,
	useWalletsQuery,
} from "../../services/queryHooks";
import { Loading } from "../../components/common/Loading";
import { EnhancedTableToolbar } from "./EnhancedTableToolbar";
import AddTransactionModal from "./AddTransactionModal";
import CategoriesList from "./CategoryList/CategoriesList";
import TransactionsTable from "./TransactionsTable";
import { useTranslation } from "react-i18next";
import { filterDatePeriods } from "./filterDatePeriods";

export default function Transactions({
	customTransactions,
	customTransactionsLoading,
	simplifiedTable = false,
}) {
	const theme = useTheme();
	const { t } = useTranslation();
	const [order, setOrder] = useState("desc");
	const [orderBy, setOrderBy] = useState("date");
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [page, setPage] = useState(0);
	const { data: categories, isPending: categoriesLoading } =
		useCategoriesQuery();

	const { data: wallets, isPending: walletsLoading } = useWalletsQuery();

	const [isAddTransactionModalOpen, setIsAddTransactionModalOpen] =
		useState(false);

	const [filterParams, setFilterParams] = useState({
		datePeriod: filterDatePeriods[0],
		searchPhrase: "",
		walletFilters: [],
		typeFilters: [],
		importantFilters: [],
	});

	const shouldFetch = !customTransactions && !simplifiedTable;

	const { data: transactions, isPending: transactionsLoading } =
		useTransactionsQuery(
			shouldFetch
				? filterParams.datePeriod?.startDate?.toISOString()
				: null,
			shouldFetch
				? filterParams.datePeriod?.endDate?.toISOString()
				: null,
			null,
			undefined,
			shouldFetch
		);

	const [selectedType, setSelectedType] = useState(null);
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [selectedSubcategory, setSelectedSubcategory] = useState(null);

	const descendingComparator = (a, b, orderBy) => {
		if (orderBy === "date") {
			return new Date(b.createdAt) - new Date(a.createdAt);
		} else if (orderBy === "amount") {
			return b.amount - a.amount;
		}
	};

	const getComparator = useCallback((order, orderBy) => {
		return order === "desc"
			? (a, b) => descendingComparator(a, b, orderBy)
			: (a, b) => -descendingComparator(a, b, orderBy);
	}, []);

	const groupTransactions = (transactions) => {
		const idMap = new Map(transactions.map((t) => [t._id, t]));

		const groups = [];
		const used = new Set();

		for (const tx of transactions) {
			if (used.has(tx._id)) continue;

			const child = tx.investmentDebtPayoffTransactionId
				? idMap.get(tx.investmentDebtPayoffTransactionId)
				: null;

			if (child) {
				groups.push([tx, child]);
				used.add(tx._id);
				used.add(child._id);
			} else {
				groups.push([tx]);
				used.add(tx._id);
			}
		}

		return groups;
	};

	const sortGroups = (groups, comparator) => {
		groups.sort((a, b) => comparator(a[0], b[0]));
	};

	const flattenGroups = (groups) => {
		return groups.flat();
	};

	const sortTransactions = useCallback(
		(transactions, order, orderBy) => {
			const comparator = getComparator(order, orderBy);
			const groups = groupTransactions(transactions);
			sortGroups(groups, comparator);

			return flattenGroups(groups);
		},
		[getComparator]
	);

	const addFilterParams = (paramName, paramValue) => {
		if (paramName === "typeFilters" || paramName === "importantFilters") {
			setFilterParams((prev) => ({ ...prev, [paramName]: [paramValue] }));
		} else if (
			paramName === "walletFilters" &&
			!filterParams.walletFilters?.includes(paramValue)
		) {
			setFilterParams((prev) => ({
				...prev,
				[paramName]: [...filterParams.walletFilters, paramValue],
			}));
		} else if (paramName === "searchPhrase" || paramName === "datePeriod") {
			setFilterParams((prev) => ({ ...prev, [paramName]: paramValue }));
		}
	};

	const removeFilterParams = (paramName, paramValue) => {
		setFilterParams((prev) => ({
			...prev,
			[paramName]: filterParams[paramName].filter(
				(item) => item !== paramValue
			),
		}));
	};

	const [transactionsFiltered, setTransactionsFiltered] = useState(
		transactionsLoading || customTransactionsLoading
			? []
			: customTransactions?.length > 0
				? customTransactions
				: transactions
	);

	const filterTransactions = useCallback(() => {
		if (
			(transactionsLoading && !customTransactions) ||
			customTransactionsLoading
		)
			return;

		const source = customTransactions?.length
			? customTransactions
			: (transactions ?? []);

		const filtered = customTransactions
			? source
			: source.filter(
					(item) =>
						new Date(item.createdAt) >=
							filterParams.datePeriod?.startDate &&
						new Date(item.createdAt) <
							filterParams.datePeriod?.endDate
				);

		setTransactionsFiltered(
			filtered.filter((item) => {
				if (filterParams.searchPhrase !== "") {
					if (
						!item.description
							.toLowerCase()
							.includes(filterParams.searchPhrase.toLowerCase())
					) {
						return false;
					}
				}
				if (filterParams.walletFilters.length) {
					if (!filterParams.walletFilters.includes(item.walletId))
						return false;
				}
				if (filterParams.typeFilters.length) {
					if (
						!filterParams.typeFilters.some(
							(type) =>
								type.toLowerCase() === item.type.toLowerCase()
						)
					)
						return false;
				}
				if (filterParams.importantFilters.length) {
					if (
						filterParams.importantFilters.includes("important") &&
						filterParams.importantFilters.length === 1 &&
						!item.includeInAnalysis
					)
						return false;
					if (
						filterParams.importantFilters.includes("unimportant") &&
						filterParams.importantFilters.length === 1 &&
						item.includeInAnalysis
					)
						return false;
				}
				return true;
			})
		);
	}, [
		customTransactions,
		customTransactionsLoading,
		filterParams.searchPhrase,
		filterParams.walletFilters,
		filterParams.typeFilters,
		transactions,
		transactionsLoading,
		filterParams.importantFilters,
		filterParams.datePeriod,
	]);

	useEffect(() => {
		filterTransactions();
	}, [
		filterParams.datePeriod,
		filterParams.searchPhrase,
		filterParams.walletFilters,
		filterParams.typeFilters,
		filterParams.importantFilters,
		filterTransactions,
	]);

	const visibleRows = useMemo(() => {
		const filtered = transactionsFiltered.filter((item) => {
			if (selectedSubcategory) {
				return item.subcategoryId === selectedSubcategory?._id;
			} else if (selectedCategory) {
				return item.categoryId === selectedCategory?._id;
			} else if (selectedType) {
				return item.type === selectedType;
			}
			return true;
		});
		const sorted = sortTransactions(filtered, order, orderBy);
		return sorted.slice(
			page * rowsPerPage,
			page * rowsPerPage + rowsPerPage
		);
	}, [
		transactionsFiltered,
		order,
		orderBy,
		page,
		rowsPerPage,
		selectedType,
		selectedCategory,
		selectedSubcategory,
		sortTransactions,
	]);

	const filteredCategories = useMemo(
		() =>
			categories
				?.map((category) => {
					const filteredSubcategories = category.subcategories.filter(
						(subcategory) =>
							visibleRows.some(
								(t) => t.subcategoryId === subcategory._id
							)
					);

					const hasMatchingCategory = visibleRows.some(
						(t) => t.categoryId === category._id
					);
					if (
						hasMatchingCategory ||
						filteredSubcategories.length > 0
					) {
						return {
							...category,
							subcategories: filteredSubcategories,
						};
					}
					return null;
				})
				.filter(Boolean),
		[categories, visibleRows]
	);

	const leftBoxStyles = {
		display: "flex",
		flex: 1,
		bgcolor: theme.palette.background.paper,
		borderRadius: "10px",
		alignSelf: "start",
	};

	const tableBoxStyles = {
		display: "flex",
		flexDirection: "column",
		flex: 2,
		alignSelf: "start",
		backgroundColor: theme.palette.background.paper,
		borderRadius: "10px",
	};

	const transactionBoxStyles = {
		display: "flex",
		flexWrap: "wrap",
		gap: 2,
		m: simplifiedTable ? 0 : 2,
		flex: simplifiedTable ? 1 : "unset",
	};

	if (categoriesLoading || walletsLoading) {
		return <Loading />;
	}
	if (!simplifiedTable && transactionsLoading) {
		return <Loading />;
	}

	return (
		<>
			{!simplifiedTable && (
				<AddTransactionModal
					isOpen={isAddTransactionModalOpen}
					onClose={() => setIsAddTransactionModalOpen(false)}
					wallets={wallets}
				/>
			)}
			<Box sx={transactionBoxStyles}>
				{!simplifiedTable && (
					<Box sx={leftBoxStyles}>
						<CategoriesList
							categories={filteredCategories}
							transactions={visibleRows}
							selectedType={selectedType}
							setSelectedType={setSelectedType}
							selectedCategory={selectedCategory}
							setSelectedCategory={setSelectedCategory}
							selectedSubcategory={selectedSubcategory}
							setSelectedSubcategory={setSelectedSubcategory}
						/>
					</Box>
				)}
				<Box sx={tableBoxStyles}>
					{!simplifiedTable && (
						<EnhancedTableToolbar
							titleText={t("transactions")}
							onAddClick={() =>
								setIsAddTransactionModalOpen(true)
							}
							filterParams={filterParams}
							addFilterParams={addFilterParams}
							removeFilterParams={removeFilterParams}
							wallets={wallets}
							selectedType={selectedType}
							setSelectedType={setSelectedType}
							selectedCategory={selectedCategory}
							setSelectedCategory={setSelectedCategory}
							selectedSubcategory={selectedSubcategory}
							setSelectedSubcategory={setSelectedSubcategory}
						/>
					)}
					{simplifiedTable && (
						<Typography
							variant="h6"
							sx={{ p: 2, mb: 0, textAlign: "left" }}
						>
							{t("transactions")}
						</Typography>
					)}

					<TransactionsTable
						order={order}
						setOrder={setOrder}
						orderBy={orderBy}
						setOrderBy={setOrderBy}
						visibleRows={visibleRows}
						transactionsLoading={transactionsLoading}
						page={page}
						setPage={setPage}
						rowsPerPage={rowsPerPage}
						setRowsPerPage={setRowsPerPage}
						transactionsFiltered={transactionsFiltered}
						wallets={wallets}
					/>
				</Box>
			</Box>
		</>
	);
}
